/**
 * WordPuzzle.js — Crossword Word Puzzle Game for DarijaPro
 * Standalone module: letter circle + crossword grid + bonus basket + coins/hints
 * Mobile-first: everything fits on one screen (100vh), no scrolling
 * Supports both SWIPE and TAP interaction for letter circle
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from './i18n/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Default fallback gradient (used before API backgrounds load)
const DEFAULT_BG = 'linear-gradient(180deg, #0d0221 0%, #0a0a3e 30%, #150050 50%, #3f0071 70%, #610094 100%)';

// ─── Responsive helper ───────────────────────────────────────────────────
const useWindowSize = () => {
  const [size, setSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const onResize = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return size;
};

// ─── Star display ─────────────────────────────────────────────────────────
const Stars = ({ count, max = 5, size = 16 }) => (
  <div style={{ display: 'flex', gap: 1 }}>
    {Array.from({ length: max }, (_, i) => (
      <span key={i} style={{ fontSize: size, color: i < count ? '#f59e0b' : '#374151', opacity: i < count ? 1 : 0.3 }}>
        ★
      </span>
    ))}
  </div>
);

// ─── Coin display ─────────────────────────────────────────────────────────
const CoinBadge = ({ coins, size = 'sm' }) => {
  const lg = size === 'lg';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: lg ? 6 : 4,
      background: 'rgba(0,0,0,0.4)', borderRadius: 16, padding: lg ? '5px 14px' : '3px 10px',
      border: '1px solid rgba(255,215,0,0.3)'
    }}>
      <span style={{ fontSize: lg ? 20 : 14 }}>🪙</span>
      <span style={{ color: '#ffd700', fontWeight: 700, fontSize: lg ? 17 : 12 }}>{coins}</span>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// HOW TO PLAY OVERLAY — loads steps from backend API
// ═══════════════════════════════════════════════════════════════════════════
const HowToPlay = ({ onClose }) => {
  const { t } = useTranslation();
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/word-puzzle/how-to-play`);
        if (res.ok) {
          const data = await res.json();
          setSteps(data.steps || []);
        }
      } catch (e) {
        console.error('Failed to load how-to-play:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 150,
      background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #1e1b4b, #312e81)',
        borderRadius: 20, padding: '24px 20px', maxWidth: 340, width: '100%',
        border: '1px solid rgba(255,255,255,0.15)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        maxHeight: '85vh', overflow: 'auto'
      }}>
        <h2 style={{ color: '#fff', fontSize: 20, fontWeight: 800, textAlign: 'center', marginBottom: 16 }}>{t('progress_how_to_play')}</h2>

        {loading ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: 20 }}>{t('amazigh_loading')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {steps.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                {item.icon && item.icon.startsWith('/')
                  ? <img src={`${API.replace('/api', '')}${item.icon}`} alt="" style={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0, marginTop: 1 }} />
                  : <span style={{ fontSize: 22, flexShrink: 0, marginTop: 1 }}>{item.icon}</span>}
                <div>
                  <div style={{ color: '#e5e7eb', fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{item.title}</div>
                  <div style={{ color: '#9ca3af', fontSize: 11, lineHeight: 1.4 }}>{item.description}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <button onClick={onClose} style={{
          width: '100%', marginTop: 18, padding: '12px 0', borderRadius: 12,
          background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
          border: 'none', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer'
        }}>
          Got it!
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// CROSSWORD GRID — responsive cell sizing
// ═══════════════════════════════════════════════════════════════════════════
const CrosswordGrid = ({ gridLayout, foundWords, revealedCells, maxWidth, maxHeight, gridCellMax = 34, gridOpacity = 25, gridColor = '#C8B4DC' }) => {
  if (!gridLayout || gridLayout.length === 0) return null;

  let maxRow = 0, maxCol = 0;
  gridLayout.forEach(w => {
    const endRow = w.direction === 'vertical' ? w.row + w.length - 1 : w.row;
    const endCol = w.direction === 'horizontal' ? w.col + w.length - 1 : w.col;
    maxRow = Math.max(maxRow, endRow);
    maxCol = Math.max(maxCol, endCol);
  });

  const cols = maxCol + 1;
  const rows = maxRow + 1;
  const gap = 3;
  const cellW = Math.floor((maxWidth - gap * (cols - 1)) / cols);
  const cellH = Math.floor((maxHeight - gap * (rows - 1)) / rows);
  const cellSize = Math.min(cellW, cellH, gridCellMax);

  const cells = {};
  gridLayout.forEach(w => {
    const darija = w.darija.toLowerCase();
    const isFound = foundWords.includes(darija);
    for (let i = 0; i < darija.length; i++) {
      const r = w.direction === 'vertical' ? w.row + i : w.row;
      const c = w.direction === 'horizontal' ? w.col + i : w.col;
      const key = `${r}-${c}`;
      const isRevealed = revealedCells.has(key);
      if (!cells[key] || isFound || isRevealed) {
        cells[key] = { letter: darija[i], filled: isFound || isRevealed, row: r, col: c };
      } else if (!cells[key].filled) {
        cells[key] = { letter: darija[i], filled: false, row: r, col: c };
      }
    }
  });

  const gridWidth = cols * cellSize + (cols - 1) * gap;
  const gridHeight = rows * cellSize + (rows - 1) * gap;

  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      width: '100%', height: '100%'
    }}>
      <div style={{ position: 'relative', width: gridWidth, height: gridHeight }}>
        {Object.entries(cells).map(([key, cell]) => (
          <div
            key={key}
            style={{
              position: 'absolute',
              left: cell.col * (cellSize + gap),
              top: cell.row * (cellSize + gap),
              width: cellSize, height: cellSize,
              borderRadius: Math.max(4, cellSize * 0.15),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: Math.max(10, cellSize * 0.4),
              fontWeight: 700, textTransform: 'uppercase',
              transition: 'all 0.3s ease',
              background: cell.filled
                ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
                : (() => { const r = parseInt(gridColor.slice(1,3),16), g = parseInt(gridColor.slice(3,5),16), b = parseInt(gridColor.slice(5,7),16); return `rgba(${r}, ${g}, ${b}, ${gridOpacity / 100})`; })(),
              backdropFilter: cell.filled ? 'none' : 'blur(8px)',
              WebkitBackdropFilter: cell.filled ? 'none' : 'blur(8px)',
              color: cell.filled ? '#fff' : 'transparent',
              border: cell.filled
                ? '2px solid rgba(255,255,255,0.3)'
                : `2px solid ${(() => { const r = parseInt(gridColor.slice(1,3),16), g = parseInt(gridColor.slice(3,5),16), b = parseInt(gridColor.slice(5,7),16); return `rgba(${r}, ${g}, ${b}, 0.15)`; })()}`,
              boxShadow: cell.filled
                ? '0 2px 8px rgba(37,99,235,0.4)'
                : 'inset 0 1px 3px rgba(0,0,0,0.2)'
            }}
          >
            {cell.filled ? cell.letter : ''}
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// LETTER CIRCLE — Supports BOTH tap and swipe
// ═══════════════════════════════════════════════════════════════════════════
const LetterCircle = ({ letters, onWordComplete, disabled, radius, onClear, onSelectionChange, hexSize = 34, hexGap = 10, hexStyle = null }) => {
  const [selectedIndices, setSelectedIndices] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const hasMoved = useRef(false);
  const circleRef = useRef(null);
  const letterRefs = useRef([]);

  // Notify parent when selection changes (for disabling shuffle)
  useEffect(() => {
    if (onSelectionChange) onSelectionChange(selectedIndices.length > 0);
  }, [selectedIndices, onSelectionChange]);

  const currentWord = selectedIndices.map(i => letters[i]).join('');


  // Always use honeycomb grid — consistent look for all levels
  const useGrid = true;

  // Grid mode: honeycomb rows — size/gap from admin settings
  const gridBtnSize = hexSize;
  const gridGap = hexGap;
  const gridCols = Math.min(6, Math.ceil(letters.length / 2));
  const gridRows = [];
  if (useGrid) {
    let remaining = letters.length;
    let rowIdx = 0;
    while (remaining > 0) {
      // Alternate between gridCols and gridCols-1 for honeycomb offset
      const cols = rowIdx % 2 === 0 ? gridCols : gridCols - 1;
      const count = Math.min(cols, remaining);
      gridRows.push(count);
      remaining -= count;
      rowIdx++;
    }
  }

  const btnSize = gridBtnSize;

  // Position: circle mode uses angle, grid mode uses row/col
  const getLetterPosition = (index, total, r) => {
    const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
    return { x: Math.cos(angle) * r, y: Math.sin(angle) * r };
  };

  // Grid positions: honeycomb packing (rows overlap at 75% height)
  const hexRowH = gridBtnSize * 0.92; // hexagons pack tighter vertically
  const getGridPosition = (i) => {
    let idx = 0;
    for (let row = 0; row < gridRows.length; row++) {
      const cols = gridRows[row];
      if (i < idx + cols) {
        const col = i - idx;
        const rowWidth = cols * (gridBtnSize + gridGap) - gridGap;
        const totalWidth = gridCols * (gridBtnSize + gridGap) - gridGap;
        const x = col * (gridBtnSize + gridGap) + (totalWidth - rowWidth) / 2 + gridBtnSize / 2;
        const y = row * hexRowH + gridBtnSize / 2;
        return { x, y };
      }
      idx += cols;
    }
    return { x: 0, y: 0 };
  };

  const findLetterAtPoint = (clientX, clientY) => {
    for (let i = 0; i < letterRefs.current.length; i++) {
      const el = letterRefs.current[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dist = Math.sqrt((clientX - cx) ** 2 + (clientY - cy) ** 2);
      if (dist < btnSize * 0.7) return i;
    }
    return -1;
  };

  const handleDragStart = (index) => {
    if (disabled) return;
    if (selectedIndices.length > 0 && !isDragging) {
      // Tap any selected letter to remove it from the word
      if (selectedIndices.includes(index)) {
        setSelectedIndices(prev => prev.filter(i => i !== index));
        return;
      }
      // Add new letter
      setSelectedIndices(prev => [...prev, index]);
      return;
    }
    setIsDragging(true);
    hasMoved.current = false;
    setSelectedIndices([index]);
  };

  const handleDragMove = (clientX, clientY) => {
    if (!isDragging || disabled) return;
    const idx = findLetterAtPoint(clientX, clientY);
    if (idx >= 0 && !selectedIndices.includes(idx)) {
      hasMoved.current = true;
      setSelectedIndices(prev => [...prev, idx]);
    }
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (hasMoved.current && currentWord.length >= 2) {
      onWordComplete(currentWord);
      setSelectedIndices([]);
      return;
    }
  };

  const handleSubmit = () => {
    if (currentWord.length >= 2) onWordComplete(currentWord);
    setSelectedIndices([]);
  };

  const handleClearSelection = () => setSelectedIndices([]);

  const onTouchStart = (e, index) => { e.preventDefault(); handleDragStart(index); };
  const onTouchMove = (e) => { e.preventDefault(); handleDragMove(e.touches[0].clientX, e.touches[0].clientY); };
  const onTouchEnd = (e) => { e.preventDefault(); handleDragEnd(); };
  const onMouseDown = (index) => handleDragStart(index);
  const onMouseMove = (e) => handleDragMove(e.clientX, e.clientY);
  const onMouseUp = () => handleDragEnd();

  useEffect(() => {
    if (!isDragging) return;
    const handler = () => handleDragEnd();
    window.addEventListener('mouseup', handler);
    return () => window.removeEventListener('mouseup', handler);
  }, [isDragging]);

  // Container size
  const gridTotalW = gridCols * (gridBtnSize + gridGap) - gridGap;
  const gridTotalH = (gridRows.length - 1) * (gridBtnSize * 0.92) + gridBtnSize;
  const containerW = gridTotalW + 16;
  const containerH = gridTotalH + 16;

  // Get absolute position for letter i within the container
  const getAbsPos = (i) => {
    const gp = getGridPosition(i);
    return { x: gp.x + 8, y: gp.y + 8 };
  };

  const getLinePoints = () => {
    if (selectedIndices.length < 2) return [];
    return selectedIndices.slice(1).map((_, i) => {
      const from = getAbsPos(selectedIndices[i]);
      const to = getAbsPos(selectedIndices[i + 1]);
      return { x1: from.x, y1: from.y, x2: to.x, y2: to.y };
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Word display — inside the box, compact */}
      <div style={{
        minHeight: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        marginBottom: 4, maxWidth: containerW, width: '100%'
      }}>
        {currentWord.length > 0 && !isDragging && (
          <button onClick={handleClearSelection} style={{
            background: 'rgba(239,68,68,0.3)', border: '1px solid rgba(239,68,68,0.5)',
            color: '#fca5a5', borderRadius: 6, padding: '4px 10px',
            cursor: 'pointer', fontSize: 12, fontWeight: 600, flexShrink: 0
          }}>
            ✕
          </button>
        )}
        <div style={{
          fontSize: Math.min(18, radius * 0.2), fontWeight: 700, color: '#fff',
          letterSpacing: 2, textTransform: 'uppercase', textShadow: '0 2px 4px rgba(0,0,0,0.5)',
          textAlign: 'center', overflow: 'hidden', whiteSpace: 'nowrap', flex: 1
        }}>
          {currentWord || '\u00A0'}
        </div>
        {currentWord.length >= 2 && !isDragging && (
          <button onClick={handleSubmit} style={{
            background: 'rgba(16,185,129,0.4)', border: '1px solid rgba(16,185,129,0.6)',
            color: '#6ee7b7', borderRadius: 6, padding: '4px 10px',
            cursor: 'pointer', fontSize: 12, fontWeight: 700, flexShrink: 0
          }}>
            ✓
          </button>
        )}
      </div>

      {/* Letter area — circle for ≤10 letters, honeycomb grid for 11+ */}
      <div
        ref={circleRef}
        onMouseMove={onMouseMove}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          position: 'relative', width: containerW, height: containerH,
          borderRadius: useGrid ? 16 : '50%',
          background: useGrid
            ? 'transparent'
            : 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 70%, transparent 100%)',
          border: useGrid ? 'none' : '2px solid rgba(255,255,255,0.08)',
          touchAction: 'none', userSelect: 'none'
        }}
      >
        {/* connector lines removed — cleaner look */}

        {letters.map((letter, i) => {
          const pos = getAbsPos(i);
          const isSelected = selectedIndices.includes(i);
          return (
            <div
              key={i}
              ref={el => letterRefs.current[i] = el}
              onMouseDown={() => onMouseDown(i)}
              onTouchStart={(e) => onTouchStart(e, i)}
              style={{
                position: 'absolute',
                left: pos.x - btnSize / 2,
                top: pos.y - btnSize / 2,
                width: btnSize, height: btnSize,
                clipPath: useGrid ? 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' : 'none',
                borderRadius: useGrid ? 0 : '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: Math.max(16, btnSize * 0.48),
                fontWeight: 800, textTransform: 'uppercase',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                background: isSelected
                  ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
                  : useGrid ? (hexStyle?.hex_bg || 'linear-gradient(180deg, rgba(255,255,255,0.95), rgba(230,230,240,0.9))') : 'rgba(255,255,255,0.9)',
                color: isSelected ? '#fff' : (hexStyle?.hex_text || '#1e293b'),
                boxShadow: isSelected
                  ? '0 0 8px rgba(59,130,246,0.4)'
                  : '0 2px 4px rgba(0,0,0,0.2)',
                filter: !isSelected && hexStyle?.hex_border ? `drop-shadow(0 0 3px ${hexStyle.hex_border})` : 'none',
                transform: 'scale(1)',
                border: 'none'
              }}
            >
              {letter}
              {isSelected && (
                <span style={{
                  position: 'absolute', top: -4, right: -4,
                  background: '#1d4ed8', color: '#fff', fontSize: 8, fontWeight: 700,
                  width: 14, height: 14, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  ✓
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// BONUS JAR
// ═══════════════════════════════════════════════════════════════════════════
const WordBasket = ({ count, total, justFilled, btnSize = 40, bonusWords = [], showParticle = false, showConfetti = false }) => {
  const [showPopup, setShowPopup] = useState(false);
  const confettiColors = ['#fbbf24','#f87171','#34d399','#60a5fa','#a78bfa','#fb923c'];
  return (
    <div style={{ position: 'relative' }}>
      {/* Floating +1 particle */}
      {showParticle && (
        <span style={{
          position: 'absolute', bottom: btnSize + 4, left: '50%', transform: 'translateX(-50%)',
          color: '#fbbf24', fontWeight: 700, fontSize: 14, zIndex: 100, pointerEvents: 'none',
          animation: 'floatUp 0.8s ease forwards'
        }}>+1</span>
      )}
      {/* Confetti burst */}
      {showConfetti && (
        <div style={{ position: 'absolute', bottom: btnSize / 2, left: '50%', zIndex: 100, pointerEvents: 'none' }}>
          {Array.from({length: 10}, (_, i) => {
            const dx = (Math.random() - 0.5) * 120;
            const dy = -(Math.random() * 80 + 20);
            return (
              <div key={i} style={{
                position: 'absolute', width: 6, height: 6, borderRadius: '50%',
                background: confettiColors[i % confettiColors.length],
                animation: `confettiBurst 1s ${Math.random() * 0.2}s ease forwards`,
                '--dx': dx + 'px', '--dy': dy + 'px'
              }} />
            );
          })}
        </div>
      )}
      <div
        onClick={() => setShowPopup(prev => !prev)}
        style={{
          position: 'relative',
          background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(251,191,36,0.4)',
          borderRadius: '50%', width: btnSize, height: btnSize,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          lineHeight: 1, cursor: 'pointer'
        }}
      >
        <span style={{ fontSize: btnSize * 0.38, animation: justFilled ? 'pulse 0.5s ease' : 'none' }}>🧺</span>
        <span style={{ fontSize: btnSize * 0.18, fontWeight: 700, color: '#fbbf24' }}>{count}/{total}</span>
        {count > 0 && (
          <div style={{
            position: 'absolute', top: -4, right: -4,
            background: justFilled ? '#10b981' : '#ef4444',
            color: '#fff', fontSize: btnSize * 0.2, fontWeight: 700,
            width: btnSize * 0.35, height: btnSize * 0.35, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            {count}
          </div>
        )}
      </div>
      {showPopup && (
        <>
          <div onClick={() => setShowPopup(false)} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
          <div style={{
            position: 'absolute', bottom: btnSize + 8, left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(10,10,30,0.92)', border: '1px solid rgba(251,191,36,0.3)',
            borderRadius: 10, padding: '8px 12px', zIndex: 50,
            minWidth: 120, maxHeight: 150, overflowY: 'auto',
            boxShadow: '0 4px 15px rgba(0,0,0,0.4)'
          }}>
            {bonusWords.length > 0 ? (
              <>
                <div style={{ fontSize: 11, color: '#fbbf24', fontWeight: 700, marginBottom: 4 }}>
                  Bonus Words ({bonusWords.length})
                </div>
                {bonusWords.map((w, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#fff', padding: '2px 0', textTransform: 'capitalize' }}>
                    {w}
                  </div>
                ))}
              </>
            ) : (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                No bonus words yet
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// LETTER TRACKER — shows remaining uses for duplicate letters
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// WORD BANK (English hints) — compact overlay
// ═══════════════════════════════════════════════════════════════════════════
const WordBank = ({ wordHints, foundWords, gridLayout, compact, showReveal, revealCount = 0 }) => {
  // Shuffle word order each time the component mounts (each visit)
  const shuffledHints = useState(() => {
    const indexed = wordHints.map((h, i) => ({ ...h, _origIdx: i }));
    for (let i = indexed.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indexed[i], indexed[j]] = [indexed[j], indexed[i]];
    }
    return indexed;
  })[0];

  const [revealed, setRevealed] = useState({});
  const toggleReveal = (origIdx) => setRevealed(prev => ({ ...prev, [origIdx]: !prev[origIdx] }));

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: compact ? 4 : 8,
      padding: compact ? '8px 10px' : '12px 16px'
    }}>
      <div style={{
        fontSize: compact ? 11 : 16, fontWeight: 700, color: '#fbbf24',
        textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.15)',
        paddingBottom: 6, letterSpacing: 1
      }}>
        Word Bank
      </div>
      {shuffledHints.map((hint) => {
        const oi = hint._origIdx;
        const layout = gridLayout.find(g => g.english === hint.english);
        const darija = layout ? layout.darija.toLowerCase() : '';
        const isFound = foundWords.includes(darija);
        const wordLen = hint.length;
        const stars = wordLen <= 3 ? 1 : wordLen <= 4 ? 2 : wordLen <= 5 ? 3 : wordLen <= 6 ? 4 : 5;

        return (
          <div key={oi} style={{
            opacity: isFound ? 0.4 : 1, transition: 'opacity 0.3s'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 4 : 6 }}>
              <Stars count={stars} max={5} size={compact ? 9 : 14} />
              <span style={{
                fontSize: compact ? 12 : 17, color: isFound ? '#6b7280' : '#e5e7eb',
                textDecoration: isFound ? 'line-through' : 'none',
                fontWeight: isFound ? 400 : 600, flex: 1
              }}>
                {hint.english}
              </span>
              {showReveal && !isFound && hint.darija_hint && (
                <button onClick={() => toggleReveal(oi)} style={{
                  background: revealed[oi] ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.1)',
                  border: 'none', borderRadius: 4, padding: '1px 4px',
                  cursor: 'pointer', fontSize: compact ? 10 : 12,
                  color: revealed[oi] ? '#34d399' : '#9ca3af',
                  lineHeight: 1
                }}>
                  {revealed[oi] ? '✓' : '\ud83d\udc41'}
                </button>
              )}
            </div>
            {showReveal && revealed[oi] && hint.darija_hint && !isFound && (
              <div style={{
                fontSize: compact ? 10 : 11, color: '#34d399', fontWeight: 600,
                marginLeft: compact ? 24 : 28, marginTop: 1,
                fontStyle: 'italic'
              }}>
                = {hint.darija_hint}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// LEVEL SELECT SCREEN
// ═══════════════════════════════════════════════════════════════════════════
const LevelSelect = ({ levels, progress, onSelectLevel, onBack, onHowToPlay, onResetLevel, onResetAll, containerStyle, trainingTitle, trainingDescription, trainingIcon, trainingVisible = true }) => {
  const scrollRef = useRef(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const { t } = useTranslation();

  const diffColors = { easy: '#10b981', medium: '#f59e0b', hard: '#ef4444' };
  const diffBg = { easy: 'rgba(16,185,129,0.25)', medium: 'rgba(245,158,11,0.25)', hard: 'rgba(239,68,68,0.25)' };

  return (
  <div style={{
    ...containerStyle,
    background: DEFAULT_BG,
    display: 'flex', flexDirection: 'column', color: '#fff', overflow: 'hidden'
  }}>
    {/* Header */}
    <div style={{ padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
      <button onClick={onBack} style={{
        background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
        color: '#fff', borderRadius: 10, padding: '6px 14px', cursor: 'pointer', fontSize: 13
      }}>
        ← {t('games')}
      </button>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button onClick={onResetAll} style={{
          background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)',
          color: '#f87171', borderRadius: 10, padding: '6px 10px', cursor: 'pointer',
          fontSize: 13
        }}>
          ⚙
        </button>
        <button onClick={onHowToPlay} style={{
          background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.4)',
          color: '#93c5fd', borderRadius: 10, padding: '6px 12px', cursor: 'pointer',
          fontSize: 12, fontWeight: 600
        }}>
          ? {t('progress_how_to_play')}
        </button>
        <CoinBadge coins={progress.coins} />
      </div>
    </div>

    <h1 style={{ textAlign: 'center', fontSize: 22, fontWeight: 800, margin: '2px 0 2px' }}>
      Word Puzzle
    </h1>
    <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 11, margin: '0 0 6px' }}>
      Swipe or tap Darija letters (Latin script) to solve the crossword
    </p>

    {/* Training instruction box — controlled by admin toggle */}
    {trainingVisible && levels.length > 0 && (
      <div style={{
        margin: '0 20px 10px', padding: '10px 14px', borderRadius: 12,
        background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)',
        display: 'flex', alignItems: 'flex-start', gap: 10
      }}>
        {trainingIcon
          ? <img src={`${API.replace('/api', '')}${trainingIcon}`} alt="" style={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }} />
          : <span style={{ fontSize: 20, flexShrink: 0 }}>📖</span>}
        <div>
          <div style={{ color: '#fbbf24', fontSize: 13, fontWeight: 700, marginBottom: 2 }}>
            {trainingTitle || 'Start with Level 1 (Training)'}
          </div>
          <div style={{ color: '#d1d5db', fontSize: 11, lineHeight: 1.4 }}>
            {trainingDescription || 'Learn how the puzzle works before playing! We use Latin script (abc, not Arabic).'}
          </div>
        </div>
      </div>
    )}

    {/* Level grid — infinite scroll */}
    <div ref={scrollRef} onScroll={(e) => setShowBackToTop(e.target.scrollTop > 200)}
      style={{ flex: 1, overflow: 'auto', padding: '0 16px 16px', position: 'relative' }}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))',
        gap: 10, maxWidth: 420, margin: '0 auto'
      }}>
        {levels.map((level) => {
          const isLocked = !level.unlocked;
          const isCompleted = level.completed;
          const diff = level.difficulty || 'easy';
          return (
            <button
              key={level.id}
              onClick={() => !isLocked && onSelectLevel(level)}
              disabled={isLocked}
              style={{
                position: 'relative', aspectRatio: '1', borderRadius: 14,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 2,
                cursor: isLocked ? 'not-allowed' : 'pointer',
                touchAction: 'manipulation',
                border: isCompleted
                  ? '2px solid rgba(16,185,129,0.5)'
                  : isLocked ? '2px solid rgba(255,255,255,0.1)' : '2px solid rgba(59,130,246,0.5)',
                background: isCompleted
                  ? 'rgba(16,185,129,0.15)'
                  : isLocked ? 'rgba(255,255,255,0.03)' : 'rgba(59,130,246,0.15)',
                opacity: isLocked ? 0.4 : 1,
                color: '#fff', fontSize: 18, fontWeight: 800
              }}
            >
              {/* Difficulty badge — top left */}
              {!isLocked && (
                <span style={{
                  position: 'absolute', top: 3, left: 3,
                  fontSize: 7, fontWeight: 800, letterSpacing: 0.3,
                  background: diffBg[diff], color: diffColors[diff],
                  borderRadius: 4, padding: '1px 4px', textTransform: 'uppercase'
                }}>
                  {diff}
                </span>
              )}
              {isLocked ? '🔒' : isCompleted ? '✓' : level.level_number}
              <span style={{ fontSize: 8, color: '#9ca3af', fontWeight: 400, textAlign: 'center' }}>
                {isCompleted ? 'Done' : isLocked ? 'Locked' : (level.title || `${level.word_count} words`)}
              </span>
              {isCompleted && onResetLevel && (
                <span
                  onClick={(e) => { e.stopPropagation(); onResetLevel(level); }}
                  style={{
                    position: 'absolute', top: 3, right: 3,
                    fontSize: 11, cursor: 'pointer', opacity: 0.6,
                    background: 'rgba(0,0,0,0.3)', borderRadius: '50%',
                    width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >↻</span>
              )}
            </button>
          );
        })}
      </div>
    </div>

    {/* Back to top button */}
    {showBackToTop && (
      <button onClick={() => scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
        style={{
          position: 'absolute', bottom: 20, right: 20, zIndex: 10,
          width: 40, height: 40, borderRadius: '50%',
          background: 'rgba(59,130,246,0.8)', border: '1px solid rgba(255,255,255,0.3)',
          color: '#fff', fontSize: 18, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          animation: 'fadeIn 0.2s ease'
        }}>
        ↑
      </button>
    )}
  </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// LEVEL COMPLETE SCREEN
// ═══════════════════════════════════════════════════════════════════════════
const LevelComplete = ({ levelNumber, coinsEarned, solvedPercent, onNext, onLevels }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 100,
    background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(10px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  }}>
    <div style={{
      background: 'linear-gradient(135deg, #1e1b4b, #312e81)',
      borderRadius: 24, padding: 28, textAlign: 'center',
      maxWidth: 300, width: '90%', border: '1px solid rgba(255,255,255,0.1)',
      boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
    }}>
      <div style={{ fontSize: 44, marginBottom: 8 }}>🎉</div>
      <h2 style={{ color: '#fff', fontSize: 22, fontWeight: 800, marginBottom: 4 }}>
        Level {levelNumber} Complete!
      </h2>
      <p style={{ color: '#9ca3af', fontSize: 12, marginBottom: 12 }}>
        Solved by {solvedPercent}% of players
      </p>
      <Stars count={3} max={3} size={24} />
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        marginTop: 12, marginBottom: 20
      }}>
        <span style={{ fontSize: 22 }}>🪙</span>
        <span style={{ color: '#ffd700', fontSize: 18, fontWeight: 700 }}>+{coinsEarned}</span>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onLevels} style={{
          flex: 1, padding: '10px 14px', borderRadius: 12,
          background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
          color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer'
        }}>
          Levels
        </button>
        <button onClick={onNext} style={{
          flex: 1, padding: '10px 14px', borderRadius: 12,
          background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
          border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer'
        }}>
          Next →
        </button>
      </div>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// NOTIFICATION TOAST
// ═══════════════════════════════════════════════════════════════════════════
const Toast = ({ message, type, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 2000);
    return () => clearTimeout(t);
  }, [onDone]);

  const bg = type === 'correct' ? 'rgba(16,185,129,0.9)'
    : type === 'bonus' ? 'rgba(245,158,11,0.9)'
    : type === 'error' ? 'rgba(239,68,68,0.9)'
    : 'rgba(59,130,246,0.9)';

  return (
    <div style={{
      position: 'fixed', top: 110, left: '50%', transform: 'translateX(-50%)',
      zIndex: 200, background: bg, color: '#fff', padding: '8px 20px',
      borderRadius: 10, fontSize: 13, fontWeight: 600,
      boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
      animation: 'slideDown 0.3s ease'
    }}>
      {message}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN GAME COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const WordPuzzleGame = ({ userToken, isDarkMode, setCurrentView, showNotification, onBack }) => {
  const { t } = useTranslation();
  const { w: vw, h: vh } = useWindowSize();
  const isMobile = vw < 1024;

  // Hide bottom nav + prevent background scrolling on mobile when puzzle is open
  useEffect(() => {
    document.body.classList.add('hide-bottom-nav');
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.classList.remove('hide-bottom-nav');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  // App-shell caches: read synchronously so levels + progress render from
  // frame 1 instead of the full-page emoji spinner on Ctrl+R.
  const _readWpCache = (key) => {
    try { const c = localStorage.getItem(key); if (c) return JSON.parse(c); } catch {}
    return null;
  };
  const _cachedLevels = _readWpCache('wpLevelsCache');
  const _cachedProgress = _readWpCache('wpProgressCache');

  // Detect `?level=<id>` in URL — if present, render the game screen with
  // cached level data instantly so refresh inside a level doesn't flash the
  // level grid or the spinning 🧩 loading screen.
  const _wantedLevelIdFromUrl = (() => {
    try { return new URLSearchParams(window.location.search).get('level') || null; } catch { return null; }
  })();
  const _cachedLevelData = _wantedLevelIdFromUrl ? _readWpCache(`wpLevelDataCache_${_wantedLevelIdFromUrl}`) : null;

  const [screen, setScreen] = useState(_wantedLevelIdFromUrl ? 'game' : 'levels');
  const [levels, setLevels] = useState(_cachedLevels || []);
  const [progress, setProgress] = useState(_cachedProgress || { current_level: 1, coins: 50, completed_count: 0 });
  // Skip full-page emoji spinner if we have cached levels OR cached level data.
  // BUT when URL says we should be inside a specific level and we have no
  // cached level data for it, force the spinner so the auto-resume fetch
  // doesn't flash an empty honeycomb between mount and fetch-resolution.
  const _needsLevelFetch = !!_wantedLevelIdFromUrl && !_cachedLevelData;
  const [loading, setLoading] = useState((!_cachedLevels && !_cachedLevelData) || _needsLevelFetch);
  const [showHowToPlay, setShowHowToPlay] = useState(false);

  // Game state — preload from cached level data when refreshing into a level
  const [currentLevel, setCurrentLevel] = useState(_cachedLevelData || null);
  const [letters, setLetters] = useState(_cachedLevelData?.letters || []);
  const [foundWords, setFoundWords] = useState(_cachedLevelData?.found_words || []);
  const [bonusFound, setBonusFound] = useState(_cachedLevelData?.bonus_found || []);
  const [basketCount, setBasketCount] = useState(0);
  const [basketSize, setBasketSize] = useState(5);
  const [basketJustFilled, setBasketJustFilled] = useState(false);
  const [showBonusParticle, setShowBonusParticle] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [revealedCells, setRevealedCells] = useState(new Set());
  const [toast, setToast] = useState(null);
  const [showComplete, setShowComplete] = useState(false);
  const [completeData, setCompleteData] = useState(null);
  const [showBgPicker, setShowBgPicker] = useState(false);
  const [showShop, setShowShop] = useState(false);
  const [shopData, setShopData] = useState(null); // {backgrounds, hex_styles, active_background, active_hex_style, coins}
  const [activeHexStyle, setActiveHexStyle] = useState(null); // {hex_bg, hex_border, hex_text}
  // App-shell cache for /word-puzzle/settings — drives the level-select chrome
  // (training title/description/icon, hex sizes, grid colors, bg blur). Without
  // caching these flash from defaults to admin values causing the level circles
  // and training tip to bounce on Ctrl+R.
  const _cachedWpSettings = (() => {
    try { const c = localStorage.getItem('wpSettingsCache'); if (c) return JSON.parse(c); } catch {}
    return null;
  })();
  const _cachedWpUserBg = (() => {
    try { const c = localStorage.getItem('wpUserBgCache'); if (c) return JSON.parse(c); } catch {}
    return null;
  })();
  const _cachedWpApiBgs = (() => {
    try { const c = localStorage.getItem('wpApiBgsCache'); if (c) return JSON.parse(c); } catch {}
    return null;
  })();

  const [customBg, setCustomBg] = useState(_cachedWpUserBg); // override level background
  const [apiBgs, setApiBgs] = useState(_cachedWpApiBgs || []); // backgrounds from API
  const [bgBlur, setBgBlur] = useState(_cachedWpSettings?.bg_blur || 0); // blur for image backgrounds (0-20px)
  const [hexSize, setHexSize] = useState(_cachedWpSettings?.hex_size || 34);
  const [hexGap, setHexGap] = useState(_cachedWpSettings?.hex_gap || 10);
  const [gridCellMax, setGridCellMax] = useState(_cachedWpSettings?.grid_cell_max || 34);
  const [gridOpacity, setGridOpacity] = useState(_cachedWpSettings?.grid_opacity ?? 25);
  const [gridColor, setGridColor] = useState(_cachedWpSettings?.grid_color || '#C8B4DC');
  const [hasSelection, setHasSelection] = useState(false); // letters currently selected in circle
  const [shakeCircle, setShakeCircle] = useState(false);
  const [showResetAllModal, setShowResetAllModal] = useState(false);
  const [showResetLevelModal, setShowResetLevelModal] = useState(false);
  // pendingPurchase = { type: 'background' | 'hex', item: {id, name, price, ...} } | null
  // When set, the buy-confirmation modal is shown. Replaces ugly window.confirm().
  const [pendingPurchase, setPendingPurchase] = useState(null);
  const [trainingStep, setTrainingStep] = useState(-1); // -1 = not showing
  const [showWordBank, setShowWordBank] = useState(false);
  const [trainingTitle, setTrainingTitle] = useState(_cachedWpSettings?.training_title || '');
  const [trainingDescription, setTrainingDescription] = useState(_cachedWpSettings?.training_description || '');
  const [trainingIcon, setTrainingIcon] = useState(_cachedWpSettings?.training_icon || '');
  const [trainingVisible, setTrainingVisible] = useState(_cachedWpSettings?.training_visible !== undefined ? _cachedWpSettings.training_visible : true);

  const headers = { 'Authorization': `Bearer ${userToken}`, 'Content-Type': 'application/json' };

  // Mark tutorial as seen in database
  const markTutorialSeen = async () => {
    try {
      await fetch(`${API}/word-puzzle/tutorial-seen`, { method: 'POST', headers });
    } catch (e) { /* ignore */ }
  };

  // Navigate back to games page
  const goBackToGames = () => {
    if (onBack) {
      onBack();
    } else {
      window.location.href = '/games';
    }
  };

  // ── Load backgrounds from API ─────────────────────────────────────────
  const loadBackgrounds = useCallback(async () => {
    try {
      const [bgRes, settingsRes] = await Promise.all([
        fetch(`${API}/word-puzzle/backgrounds`, { headers }),
        fetch(`${API}/word-puzzle/settings`)
      ]);
      if (bgRes.ok) {
        const data = await bgRes.json();
        const bgs = data.backgrounds || [];
        setApiBgs(bgs);
        try { localStorage.setItem('wpApiBgsCache', JSON.stringify(bgs)); } catch {}
        if (data.user_bg) {
          setCustomBg(data.user_bg);
          try { localStorage.setItem('wpUserBgCache', JSON.stringify(data.user_bg)); } catch {}
        }
      }
      if (settingsRes.ok) {
        const sData = await settingsRes.json();
        setBgBlur(sData.bg_blur || 0);
        if (sData.hex_size) setHexSize(sData.hex_size);
        if (sData.hex_gap) setHexGap(sData.hex_gap);
        if (sData.grid_cell_max) setGridCellMax(sData.grid_cell_max);
        if (sData.grid_opacity !== undefined) setGridOpacity(sData.grid_opacity);
        if (sData.grid_color) setGridColor(sData.grid_color);
        if (sData.training_title) setTrainingTitle(sData.training_title);
        if (sData.training_description) setTrainingDescription(sData.training_description);
        if (sData.training_icon) setTrainingIcon(sData.training_icon);
        if (sData.training_visible !== undefined) setTrainingVisible(sData.training_visible);
        try { localStorage.setItem('wpSettingsCache', JSON.stringify(sData)); } catch {}
      }
    } catch (e) { console.error('Failed to load backgrounds:', e); }
  }, [userToken]);

  const loadShop = useCallback(async () => {
    try {
      const res = await fetch(`${API}/word-puzzle/shop`, { headers });
      if (res.ok) {
        const data = await res.json();
        setShopData(data);
        // Load active hex style colors
        if (data.active_hex_style && data.hex_styles) {
          const active = data.hex_styles.find(h => h.id === data.active_hex_style);
          if (active) setActiveHexStyle({ hex_bg: active.hex_bg, hex_border: active.hex_border, hex_text: active.hex_text });
        }
      }
    } catch (e) { console.error('Failed to load shop:', e); }
  }, [userToken]);

  // ── Load levels ───────────────────────────────────────────────────────
  const loadLevels = useCallback(async () => {
    try {
      // Only show spinner if no cached levels — refresh runs silently otherwise
      if (levels.length === 0) setLoading(true);
      const res = await fetch(`${API}/word-puzzle/levels`, { headers });
      if (res.ok) {
        const data = await res.json();
        const list = data.levels || [];
        setLevels(list);
        try { localStorage.setItem('wpLevelsCache', JSON.stringify(list)); } catch {}
        const prog = data.progress || { current_level: 1, coins: 50 };
        setProgress(prog);
        try { localStorage.setItem('wpProgressCache', JSON.stringify(prog)); } catch {}
        // Tutorial only opens when user manually clicks the ? button (admin decision 2026-05-03)
      }
    } catch (e) {
      console.error('Failed to load levels:', e);
    } finally {
      setLoading(false);
    }
  }, [userToken]);

  useEffect(() => { if (userToken) { loadLevels(); loadBackgrounds(); loadShop(); } }, [loadLevels, loadBackgrounds, loadShop, userToken]);

  // Auto-resume: if URL has ?level=<id>, jump straight into that level once
  // levels have loaded. If we already have cached level data from a previous
  // session, currentLevel was preloaded from cache — only run a silent
  // background refresh, no spinner flash.
  useEffect(() => {
    if (levels.length === 0) return;
    let wantedId;
    try { wantedId = new URLSearchParams(window.location.search).get('level'); } catch { return; }
    if (!wantedId) return;
    const lvl = levels.find(l => String(l.id) === wantedId);
    if (!lvl || !lvl.unlocked) return;
    // Cache hit — trust the cached level data, no silent refresh. Re-fetching
    // would call setLetters/setFoundWords with new array refs, causing the
    // honeycomb to visibly re-render even when data is identical. Cache is
    // refreshed next time the user actively enters this level via startLevel.
    if (currentLevel && String(currentLevel.id) === wantedId) {
      return;
    }
    // No cached level data → full startLevel (will show its own loading)
    if (!currentLevel) startLevel(lvl);
  }, [levels]); // intentionally only watches `levels` — startLevel is stable

  // ── Start level ───────────────────────────────────────────────────────
  const startLevel = async (level) => {
    try {
      // Clear stale state IMMEDIATELY so UI doesn't flash OLD level's word bank
      // during the async fetch (Bug fix 2026-05-03 — auto-advance showed old data).
      setCurrentLevel(null);
      setFoundWords([]);
      setBonusFound([]);
      setBasketCount(0);
      setRevealedCells(new Set());
      setShowComplete(false);
      setLoading(true);
      const res = await fetch(`${API}/word-puzzle/level/${level.id}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setCurrentLevel(data);
        setLetters(data.letters || []);
        setFoundWords(data.found_words || []);
        setBonusFound(data.bonus_found || []);
        setBasketCount((data.bonus_found || []).length % (data.bonus_basket_size || 5));
        setBasketSize(data.bonus_basket_size || 5);
        setRevealedCells(new Set());
        setShowComplete(false);
        setScreen('game');
        // Cache the level data so Ctrl+R inside this level renders instantly
        try { localStorage.setItem(`wpLevelDataCache_${level.id}`, JSON.stringify(data)); } catch {}
        // Encode level in URL so Ctrl+R returns directly to this level
        try { window.history.pushState({}, '', `/games/WordPuzzle?level=${level.id}`); } catch {}
        // Show training tooltips on training levels (only once)
        if (data.is_training && !progress.has_seen_training_tips) {
          setTimeout(() => setTrainingStep(0), 600);
        }
      } else {
        const err = await res.json().catch(() => ({}));
        setToast({ message: err.detail || 'Could not open level', type: 'error' });
      }
    } catch (e) {
      console.error('Failed to start level:', e);
      setToast({ message: t('wp_network_error'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ── Check word ────────────────────────────────────────────────────────
  const checkWord = async (word) => {
    if (!currentLevel) return;
    try {
      const res = await fetch(`${API}/word-puzzle/check-word`, {
        method: 'POST', headers,
        body: JSON.stringify({ level_id: currentLevel.id, word: word.toLowerCase() })
      });
      if (res.ok) {
        const data = await res.json();

        if (data.result === 'correct') {
          setFoundWords(prev => [...prev, word.toLowerCase()]);
          setToast({ message: `✓ ${data.english}`, type: 'correct' });

          if (data.level_complete) {
            setProgress(prev => ({
              ...prev,
              coins: (prev.coins || 0) + (data.coins_earned || 0),
              current_level: data.new_level
            }));
            setTimeout(() => {
              setCompleteData({
                levelNumber: currentLevel.level_number,
                coinsEarned: data.coins_earned,
                solvedPercent: data.solved_percent
              });
              setShowComplete(true);
            }, 800);
          }
        } else if (data.result === 'bonus') {
          setBonusFound(prev => [...prev, word.toLowerCase()]);
          if (data.basket_full) {
            setBasketJustFilled(true);
            setBasketCount(0);
            setProgress(prev => ({ ...prev, coins: (prev.coins || 0) + 30 }));
            setToast({ message: '🧺 Word Basket full! +30 coins', type: 'bonus' });
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 1500);
            setTimeout(() => setBasketJustFilled(false), 2000);
          } else {
            setBasketCount(data.basket_count);
            setShowBonusParticle(true);
            setTimeout(() => setShowBonusParticle(false), 800);
            setToast({ message: '🧺 Added to Word Basket!', type: 'bonus' });
          }
        } else if (data.result === 'already_found') {
          setToast({ message: t('wp_already_found'), type: 'info' });
        } else if (data.result === 'invalid') {
          setToast({ message: t('wp_not_a_word'), type: 'error' });
          setShakeCircle(true);
          setTimeout(() => setShakeCircle(false), 500);
        }
      }
    } catch (e) {
      console.error('Check word error:', e);
    }
  };

  // ── Use hint ──────────────────────────────────────────────────────────
  const useHint = async (hintType) => {
    if (!currentLevel) return;
    const cost = hintType === 'letter' ? 60 : 120;
    if (progress.coins < cost) {
      setToast({ message: `Need ${cost} coins (have ${progress.coins})`, type: 'error' });
      return;
    }

    try {
      const res = await fetch(`${API}/word-puzzle/use-hint`, {
        method: 'POST', headers,
        body: JSON.stringify({ level_id: currentLevel.id, hint_type: hintType })
      });
      if (res.ok) {
        const data = await res.json();
        setProgress(prev => ({ ...prev, coins: data.coins_remaining }));

        if (data.hint_type === 'letter') {
          setRevealedCells(prev => {
            const next = new Set(prev);
            next.add(`${data.row}-${data.col}`);
            return next;
          });
          setToast({ message: `Hint: "${data.letter}" revealed`, type: 'info' });
        } else {
          setFoundWords(prev => [...prev, data.darija.toLowerCase()]);
          setToast({ message: `Revealed: ${data.english}`, type: 'info' });
        }
      }
    } catch (e) {
      console.error('Hint error:', e);
    }
  };

  // ── Shuffle letters ───────────────────────────────────────────────────
  const shuffleLetters = () => {
    setLetters(prev => {
      const arr = [...prev];
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    });
  };

  // ── Go to next level ─────────────────────────────────────────────────
  const goNextLevel = () => {
    setShowComplete(false);
    const nextNum = (currentLevel?.level_number || 0) + 1;
    const nextLevel = levels.find(l => l.level_number === nextNum);
    if (nextLevel) {
      startLevel(nextLevel);
    } else {
      setScreen('levels');
      try { window.history.pushState({}, '', '/games/WordPuzzle'); } catch {}
      loadLevels();
    }
  };

  // ── Reset level ──────────────────────────────────────────────────────
  const resetLevel = async (level) => {
    try {
      const res = await fetch(`${API}/word-puzzle/reset-level/${level.id}`, { method: 'POST', headers });
      if (res.ok) {
        setToast({ message: `Level ${level.level_number} reset!`, type: 'info' });
        loadLevels();
      }
    } catch (e) { console.error(e); }
  };

  // ── Reset all progress ──────────────────────────────────────────────
  const resetAllProgress = async () => {
    try {
      const res = await fetch(`${API}/word-puzzle/reset-all`, {
        method: 'POST', headers,
        body: JSON.stringify({ confirm: 'RESET_ALL' })
      });
      if (res.ok) {
        // Clear local progress so tutorial replays + level grid refreshes
        setProgress(prev => ({ ...prev, current_level: 1, coins: 50, has_seen_tutorial: false }));
        setToast({ message: t('wp_all_reset'), type: 'info' });
        setShowResetAllModal(false);
        setScreen('levels');
        try { window.history.pushState({}, '', '/games/WordPuzzle'); } catch {}
        loadLevels();
      } else {
        // Show actual error so user knows what failed (was silent before)
        const err = await res.json().catch(() => ({}));
        setToast({ message: err.detail || `Reset failed (HTTP ${res.status})`, type: 'error' });
        console.error('Reset all failed:', res.status, err);
      }
    } catch (e) {
      setToast({ message: 'Reset failed: network error', type: 'error' });
      console.error(e);
    }
  };

  // ── Confirm purchase (shared by background + honeycomb buy buttons) ────
  // Called when user clicks Buy in the custom modal. Replaces native window.confirm.
  const confirmPurchase = async () => {
    if (!pendingPurchase) return;
    const { type, item } = pendingPurchase;
    const endpoint = type === 'background' ? 'buy-background' : 'buy-hex-style';
    const bodyKey = type === 'background' ? 'background_id' : 'hex_style_id';
    try {
      const res = await fetch(`${API}/word-puzzle/${endpoint}`, {
        method: 'POST', headers,
        body: JSON.stringify({ [bodyKey]: item.id })
      });
      if (res.ok) {
        const data = await res.json();
        if (type === 'background') {
          setCustomBg(item.id);
          setProgress(prev => ({ ...prev, coins: data.coins }));
          setShopData(prev => ({
            ...prev, coins: data.coins,
            backgrounds: prev.backgrounds.map(bg => bg.id === item.id ? { ...bg, unlocked: true } : bg)
          }));
        } else {
          setActiveHexStyle({ hex_bg: item.hex_bg, hex_border: item.hex_border, hex_text: item.hex_text });
          setProgress(prev => ({ ...prev, coins: data.coins }));
          setShopData(prev => ({
            ...prev, coins: data.coins, active_hex_style: item.id,
            hex_styles: prev.hex_styles.map(hs => hs.id === item.id ? { ...hs, unlocked: true } : hs)
          }));
        }
        setToast({ message: `Purchased "${item.name}"!`, type: 'success' });
      } else {
        const err = await res.json().catch(() => ({}));
        setToast({ message: err.detail || 'Purchase failed', type: 'error' });
      }
    } catch (e) {
      setToast({ message: t('wp_purchase_failed'), type: 'error' });
    } finally {
      setPendingPurchase(null);
    }
  };

  // On mobile: fixed below the app's top bar. On desktop: fill parent container.
  // On mobile: fixed below the app's top bar. On desktop: fill parent container.
  const appBarH = 90;
  const wrapperStyle = isMobile
    ? { position: 'fixed', top: appBarH, left: 0, right: 0, bottom: 0, zIndex: 40 }
    : { width: '100%', height: 'calc(100vh - 80px)', minHeight: 500, borderRadius: 16, overflow: 'hidden', position: 'relative' };

  // ── Loading screen ────────────────────────────────────────────────────
  if (loading && !currentLevel) {
    // If we know which level the user is opening (URL ?level=) and we have its
    // metadata in the cached levels array, render the loading screen with
    // THAT level's intended background image — so the user doesn't see a
    // generic purple flash before the real puzzle background loads.
    let loadingBg = DEFAULT_BG;
    if (_wantedLevelIdFromUrl && levels.length > 0) {
      const targetLevel = levels.find(l => String(l.id) === _wantedLevelIdFromUrl);
      if (targetLevel?.background) {
        const apiBg = apiBgs.find(b => b.id === targetLevel.background)
          || apiBgs.find(b => b.name?.toLowerCase().replace(/\s+/g, '_') === targetLevel.background);
        if (apiBg) {
          if (apiBg.type === 'image') {
            loadingBg = `#1a1a2e url(${API.replace('/api', '')}${apiBg.value}) center/cover no-repeat`;
          } else {
            loadingBg = apiBg.value;
          }
        }
      }
    }
    return (
      <div style={{
        ...wrapperStyle,
        background: loadingBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff'
      }}>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12, animation: 'spin 2s linear infinite' }}>🧩</div>
          <p style={{ fontSize: 13 }}>{t('wp_loading_puzzles')}</p>
        </div>
      </div>
    );
  }

  // ── Level select screen ───────────────────────────────────────────────
  if (screen === 'levels') {
    return (
      <>
        {showHowToPlay && <HowToPlay onClose={() => { setShowHowToPlay(false); markTutorialSeen(); }} />}
        {showResetAllModal && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #1e1b4b, #312e81)',
              borderRadius: 20, padding: 24, maxWidth: 320, width: '100%', textAlign: 'center',
              border: '1px solid rgba(255,255,255,0.15)'
            }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
              <h3 style={{ color: '#fff', fontSize: 18, fontWeight: 800, marginBottom: 8 }}>{t('wp_reset_all')}</h3>
              <p style={{ color: '#9ca3af', fontSize: 13, marginBottom: 20, lineHeight: 1.5 }}>
                You'll lose all coins, completed levels, and bonus words. This cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button onClick={() => setShowResetAllModal(false)} style={{
                  background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                  color: '#fff', borderRadius: 10, padding: '8px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 600
                }}>{t('cancel')}</button>
                <button onClick={resetAllProgress} style={{
                  background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                  border: 'none', color: '#fff', borderRadius: 10, padding: '8px 20px',
                  cursor: 'pointer', fontSize: 13, fontWeight: 700
                }}>{t('wp_reset_all_btn')}</button>
              </div>
            </div>
          </div>
        )}
        <LevelSelect
          levels={levels}
          progress={progress}
          onSelectLevel={startLevel}
          onBack={goBackToGames}
          onHowToPlay={() => setShowHowToPlay(true)}
          onResetLevel={resetLevel}
          onResetAll={() => setShowResetAllModal(true)}
          containerStyle={wrapperStyle}
          trainingTitle={trainingTitle}
          trainingDescription={trainingDescription}
          trainingIcon={trainingIcon}
          trainingVisible={trainingVisible}
        />
      </>
    );
  }

  // ── GAME SCREEN — single viewport, no scroll ─────────────────────────
  const bgKey = customBg || currentLevel?.background || 'desert';

  // Resolve background to CSS value from API backgrounds
  const bgToCss = (b) => {
    if (!b) return DEFAULT_BG;
    if (b.type === 'image') return `#1a1a2e url(${API.replace('/api', '')}${b.value}) center/cover no-repeat`;
    return b.value;
  };
  const resolveBackground = (key) => {
    const apiBg = apiBgs.find(b => b.id === key);
    if (apiBg) return bgToCss(apiBg);
    // If key matches a name (case-insensitive), try that
    const byName = apiBgs.find(b => b.name?.toLowerCase().replace(/\s+/g, '_') === key);
    if (byName) return bgToCss(byName);
    // Fallback: first default bg from API, or static fallback
    const defBg = apiBgs.find(b => b.is_default);
    return defBg ? bgToCss(defBg) : DEFAULT_BG;
  };
  const bgCss = resolveBackground(bgKey);
  // Check if current bg is image type (for blur overlay)
  const currentBgObj = apiBgs.find(b => b.id === bgKey) || apiBgs.find(b => b.name?.toLowerCase().replace(/\s+/g, '_') === bgKey) || apiBgs.find(b => b.is_default);
  const isImageBg = currentBgObj?.type === 'image';

  const topBarH = 58; // row1 (back+icons+coins) + row2 (title)
  // Circle: fixed comfortable size — leave 56px right margin for TikTok buttons
  const circleRadius = isMobile
    ? Math.min(95, Math.max(60, Math.floor((vw - 96) / 2.2)))
    : Math.min(160, (vw - 120) / 2.5);
  // Bottom section: word display + circle (buttons are on sides now, not below)
  const circleTotalH = 28 + (circleRadius * 2 + 40) + 16;
  // Available height for grid area (scrollable if needed)
  const gameHeight = isMobile ? vh - appBarH : Math.max(500, vh - 80);
  const gridAreaH = gameHeight - topBarH - circleTotalH;
  // Grid uses full width minus small padding
  const gridMaxW = isMobile ? Math.max(120, vw - 60) : Math.min(vw - 120, 900);

  // Desktop: use much larger sizes for grid cells, hexagons, and icon buttons
  const effectiveGridCellMax = isMobile ? gridCellMax : Math.max(gridCellMax, 56);
  const effectiveHexSize = isMobile ? hexSize : Math.max(hexSize, 44);
  const effectiveHexGap = isMobile ? hexGap : Math.max(hexGap, 10);
  const iconBtnSize = isMobile ? 40 : 56;

  return (
    <div style={{
      ...wrapperStyle,
      background: isImageBg ? '#1a1a2e' : bgCss,
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Image background with blur overlay */}
      {isImageBg && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0,
          background: bgCss,
          backgroundSize: isMobile ? 'cover' : 'contain',
          backgroundPosition: isMobile ? 'center center' : 'center center',
          filter: bgBlur > 0 ? `blur(${bgBlur}px)` : 'none',
          transform: bgBlur > 0 ? 'scale(1.1)' : 'none',
        }} />
      )}
      <style>{`
        @keyframes slideDown { from { opacity: 0; transform: translateX(-50%) translateY(-20px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.3); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 20%, 60% { transform: translateX(-6px); } 40%, 80% { transform: translateX(6px); } }
        @keyframes floatUp { from { opacity: 1; transform: translateX(-50%) translateY(0); } to { opacity: 0; transform: translateX(-50%) translateY(-30px); } }
        @keyframes confettiBurst { 0% { opacity: 1; transform: translate(0, 0) scale(1); } 100% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(0.5); } }
      `}</style>

      {showHowToPlay && <HowToPlay onClose={() => { setShowHowToPlay(false); markTutorialSeen(); }} />}
      {toast && <Toast message={toast.message} type={toast.type} onDone={() => setToast(null)} />}

      {showResetLevelModal && currentLevel && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e1b4b, #312e81)',
            borderRadius: 20, padding: 24, maxWidth: 300, width: '100%', textAlign: 'center',
            border: '1px solid rgba(255,255,255,0.15)'
          }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>↻</div>
            <h3 style={{ color: '#fff', fontSize: 16, fontWeight: 800, marginBottom: 8 }}>{t('wp_reset_level').replace('{n}', currentLevel.level_number)}</h3>
            <p style={{ color: '#9ca3af', fontSize: 12, marginBottom: 20, lineHeight: 1.5 }}>
              Found words and bonus progress for this level will be cleared.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={() => setShowResetLevelModal(false)} style={{
                background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                color: '#fff', borderRadius: 10, padding: '8px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 600
              }}>{t('cancel')}</button>
              <button onClick={async () => {
                setShowResetLevelModal(false);
                await resetLevel(currentLevel);
                startLevel(currentLevel);
              }} style={{
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                border: 'none', color: '#fff', borderRadius: 10, padding: '8px 20px',
                cursor: 'pointer', fontSize: 13, fontWeight: 700
              }}>{t('wp_reset')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Buy Confirmation Modal — replaces ugly window.confirm() ───────── */}
      {pendingPurchase && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e1b4b, #312e81)',
            borderRadius: 20, padding: 24, maxWidth: 320, width: '100%', textAlign: 'center',
            border: '1px solid rgba(255,255,255,0.15)'
          }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🛒</div>
            <h3 style={{ color: '#fff', fontSize: 16, fontWeight: 800, marginBottom: 8 }}>
              Buy "{pendingPurchase.item.name}"?
            </h3>
            <p style={{ color: '#9ca3af', fontSize: 13, marginBottom: 6 }}>
              Cost: <span style={{ color: '#fbbf24', fontWeight: 700 }}>🪙 {pendingPurchase.item.price}</span>
            </p>
            <p style={{ color: '#9ca3af', fontSize: 12, marginBottom: 20, lineHeight: 1.5 }}>
              You have 🪙 {shopData?.coins ?? progress.coins ?? 0} coins.
              After purchase: 🪙 {(shopData?.coins ?? progress.coins ?? 0) - pendingPurchase.item.price}.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={() => setPendingPurchase(null)} style={{
                background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                color: '#fff', borderRadius: 10, padding: '8px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 600
              }}>{t('cancel')}</button>
              <button onClick={confirmPurchase} style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                border: 'none', color: '#fff', borderRadius: 10, padding: '8px 20px',
                cursor: 'pointer', fontSize: 13, fontWeight: 700
              }}>Buy</button>
            </div>
          </div>
        </div>
      )}

      {/* ── TRAINING TOOLTIPS ── */}
      {trainingStep >= 0 && (() => {
        const tips = [
          { text: 'These are the English words you need to find. Stars show difficulty.', label: 'Word Bank', icon: '📋' },
          { text: 'Your answers fill in here. Letters connect like a crossword.', label: 'Crossword Grid', icon: '🔤' },
          { text: 'Tap letters to spell the Darija word. Tap ✓ to submit.', label: 'Letter Circle', icon: '⭕' },
          { text: 'Find bonus words for extra coins!', label: 'Word Basket', icon: '🧺' },
          { text: 'Stuck? Use hints — 💡 reveals a letter (60 coins), ⚡ reveals a word (120 coins).', label: 'Hints', icon: '💡' },
          ...(currentLevel?.is_training ? [{ text: 'Tap 👁 next to a word to see its Darija answer. You still need to spell it!', label: 'Reveal Button', icon: '👁' }] : [])
        ];
        const tip = tips[trainingStep];
        if (!tip) return null;
        return (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 10001,
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
          }} onClick={() => {
            if (trainingStep < tips.length - 1) {
              setTrainingStep(s => s + 1);
            } else {
              setTrainingStep(-1);
              // Mark training tips as seen
              fetch(`${API}/word-puzzle/training-tips-seen`, { method: 'POST', headers }).catch(() => {});
            }
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #1e1b4b, #312e81)',
              borderRadius: 20, padding: '24px 20px', maxWidth: 320, width: '100%',
              border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)', textAlign: 'center'
            }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>{tip.icon}</div>
              <div style={{ color: '#fbbf24', fontSize: 16, fontWeight: 800, marginBottom: 6 }}>{tip.label}</div>
              <p style={{ color: '#d1d5db', fontSize: 13, lineHeight: 1.5, marginBottom: 16 }}>{tip.text}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#6b7280', fontSize: 11 }}>
                  {trainingStep + 1} / {tips.length}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  {trainingStep < tips.length - 1 && (
                    <button onClick={() => {
                      setTrainingStep(-1);
                      fetch(`${API}/word-puzzle/training-tips-seen`, { method: 'POST', headers }).catch(() => {});
                    }} style={{
                      background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
                      color: '#9ca3af', borderRadius: 10, padding: '8px 16px',
                      cursor: 'pointer', fontSize: 13, fontWeight: 600
                    }}>
                      Skip
                    </button>
                  )}
                  <button onClick={() => {
                    if (trainingStep < tips.length - 1) {
                      setTrainingStep(s => s + 1);
                    } else {
                      setTrainingStep(-1);
                      fetch(`${API}/word-puzzle/training-tips-seen`, { method: 'POST', headers }).catch(() => {});
                    }
                  }} style={{
                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                    border: 'none', color: '#fff', borderRadius: 10, padding: '8px 20px',
                    cursor: 'pointer', fontSize: 13, fontWeight: 700
                  }}>
                    {trainingStep < tips.length - 1 ? 'Next →' : 'Got it!'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {showComplete && completeData && (
        <LevelComplete
          levelNumber={completeData.levelNumber}
          coinsEarned={completeData.coinsEarned}
          solvedPercent={completeData.solvedPercent}
          onNext={goNextLevel}
          onLevels={() => { setShowComplete(false); setScreen('levels'); try { window.history.pushState({}, '', '/games/WordPuzzle'); } catch {} loadLevels(); }}
        />
      )}

      {/* ── TOP BAR — Row 1: back + actions + coins ── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: isMobile ? '4px 12px' : '10px 24px', flexShrink: 0, position: 'relative', zIndex: 2
      }}>
        <button onClick={() => { setScreen('levels'); try { window.history.pushState({}, '', '/games/WordPuzzle'); } catch {} loadLevels(); }} style={{
          background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.15)',
          color: '#fff', borderRadius: '50%', width: iconBtnSize, height: iconBtnSize,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', fontSize: isMobile ? 15 : 20
        }}>
          ←
        </button>

        <div style={{ display: 'flex', gap: isMobile ? 5 : 8, alignItems: 'center' }}>
          <button onClick={() => { setShowShop(p => { if (!p) { loadShop(); loadBackgrounds(); } return !p; }); }} style={{
            background: showShop ? 'rgba(59,130,246,0.5)' : 'rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#fbbf24', borderRadius: '50%', width: iconBtnSize, height: iconBtnSize,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: isMobile ? 15 : 20
          }}>
            🎨
          </button>
          <button onClick={() => setShowResetLevelModal(true)} style={{
            background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.15)',
            color: '#f87171', borderRadius: '50%', width: iconBtnSize, height: iconBtnSize,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: isMobile ? 15 : 20
          }}>
            ↻
          </button>
          <button onClick={() => setTrainingStep(0)} style={{
            background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.15)',
            color: '#a78bfa', borderRadius: '50%', width: iconBtnSize, height: iconBtnSize,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: isMobile ? 15 : 20
          }}>
            📖
          </button>
          <button onClick={() => setShowHowToPlay(true)} style={{
            background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.15)',
            color: '#93c5fd', borderRadius: '50%', width: iconBtnSize, height: iconBtnSize,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: isMobile ? 15 : 20, fontWeight: 700
          }}>
            ?
          </button>
        </div>

        <CoinBadge coins={progress.coins} size={isMobile ? 'sm' : 'lg'} />
      </div>

      {currentLevel?.solved_percent > 0 && (
        <div style={{
          textAlign: 'center', padding: isMobile ? '2px 12px 6px' : '4px 24px 8px', flexShrink: 0, position: 'relative', zIndex: 2
        }}>
          <div style={{ fontSize: isMobile ? 9 : 12, color: 'rgba(255,255,255,0.4)' }}>
            Solved by {currentLevel.solved_percent}%
          </div>
        </div>
      )}


      {/* ── COIN SHOP POPUP ── */}
      {showShop && (
        <div style={{
          position: 'absolute', top: topBarH + 4, right: 8, zIndex: 30,
          background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(12px)',
          borderRadius: 14, padding: isMobile ? 10 : 14, maxWidth: isMobile ? vw - 16 : 340,
          width: isMobile ? vw - 16 : 340,
          border: '1px solid rgba(255,255,255,0.15)', maxHeight: '70vh', overflowY: 'auto'
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>🛒</span>
              <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>{t('wp_shop')}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#fbbf24', fontSize: 11, fontWeight: 700 }}>🪙 {shopData?.coins ?? progress.coins}</span>
              <button onClick={() => setShowShop(false)} style={{
                background: 'rgba(255,255,255,0.1)', border: 'none', color: '#9ca3af',
                borderRadius: '50%', width: 22, height: 22, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12
              }}>✕</button>
            </div>
          </div>

          {/* ── Backgrounds Section ── */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ color: '#94a3b8', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
              Backgrounds
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {!shopData && <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>{t('amazigh_loading')}</span>}
              {(shopData?.backgrounds || []).map(b => {
                const isActive = bgKey === b.id;
                const isUnlocked = b.unlocked;
                return (
                  <button key={b.id} onClick={async () => {
                    if (isUnlocked) {
                      // Already owned — just select it
                      setCustomBg(b.id);
                      try { await fetch(`${API}/word-puzzle/set-background`, { method: 'POST', headers, body: JSON.stringify({ background_id: b.id }) }); } catch (e) {}
                    } else {
                      // Not owned — open custom buy-confirmation modal (was window.confirm)
                      const price = b.price || 200;
                      const have = shopData?.coins ?? progress.coins ?? 0;
                      if (have < price) {
                        setToast({ message: `Need ${price - have} more coins. Save up first!`, type: 'error' });
                        return;
                      }
                      setPendingPurchase({ type: 'background', item: { ...b, price } });
                    }
                  }}
                    style={{
                      width: 52, height: 52, borderRadius: 8, position: 'relative',
                      border: isActive ? '2px solid #3b82f6' : '2px solid transparent',
                      backgroundColor: '#1a1a2e',
                      background: b.type === 'image' ? `#1a1a2e url(${API.replace('/api', '')}${b.value}) center/contain no-repeat` : b.value,
                      cursor: 'pointer', display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                      padding: 2, boxShadow: isActive ? '0 0 8px rgba(59,130,246,0.5)' : 'none',
                      opacity: isUnlocked ? 1 : 0.7
                    }}>
                    <span style={{ fontSize: 7, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.8)', textTransform: 'capitalize' }}>
                      {b.name}
                    </span>
                    {!isUnlocked && (
                      <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.5)', borderRadius: 6,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <span style={{ fontSize: 14 }}>🔒</span>
                        <span style={{ fontSize: 7, color: '#fbbf24', fontWeight: 700 }}>🪙 {b.price || 200}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Honeycomb Styles Section ── */}
          <div>
            <div style={{ color: '#94a3b8', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
              Honeycomb Colors
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(shopData?.hex_styles || []).map(h => {
                const isActive = shopData?.active_hex_style === h.id;
                const isUnlocked = h.unlocked;
                return (
                  <button key={h.id} onClick={async () => {
                    if (isUnlocked) {
                      // Already owned — switch to it
                      try {
                        await fetch(`${API}/word-puzzle/set-hex-style`, { method: 'POST', headers, body: JSON.stringify({ hex_style_id: h.id }) });
                        setActiveHexStyle({ hex_bg: h.hex_bg, hex_border: h.hex_border, hex_text: h.hex_text });
                        setShopData(prev => ({ ...prev, active_hex_style: h.id }));
                      } catch (e) {}
                    } else {
                      // Not owned — open custom buy-confirmation modal (was window.confirm)
                      const price = h.price || 200;
                      const have = shopData?.coins ?? progress.coins ?? 0;
                      if (have < price) {
                        setToast({ message: `Need ${price - have} more coins. Save up first!`, type: 'error' });
                        return;
                      }
                      setPendingPurchase({ type: 'hex', item: { ...h, price } });
                    }
                  }}
                    style={{
                      width: 60, height: 60, borderRadius: 8, position: 'relative',
                      border: isActive ? '2px solid #3b82f6' : '2px solid transparent',
                      background: h.hex_bg || 'linear-gradient(180deg, rgba(255,255,255,0.95), rgba(230,230,240,0.9))',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: 2, boxShadow: isActive ? '0 0 8px rgba(59,130,246,0.5)' : 'none',
                      clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                      opacity: isUnlocked ? 1 : 0.7
                    }}>
                    <span style={{
                      fontSize: 8, fontWeight: 700, color: h.hex_text || '#1e293b',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)', textAlign: 'center',
                      lineHeight: 1.1, maxWidth: 44, wordBreak: 'break-word'
                    }}>{h.name}</span>
                    {!isUnlocked && (
                      <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <span style={{ fontSize: 12 }}>🔒</span>
                        <span style={{ fontSize: 6, color: '#fbbf24', fontWeight: 700 }}>🪙 {h.price || 200}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── MIDDLE: Grid centered, toggle buttons + slide-out Word Bank ── */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: 0, position: 'relative', zIndex: 1, overflow: 'hidden'
      }}>
        {/* Toggle button — shows found/total count */}
        <button
          onClick={() => setShowWordBank(prev => !prev)}
          style={{
            position: 'absolute', left: 8, top: 8, zIndex: 12,
            background: showWordBank ? 'rgba(59,130,246,0.4)' : 'rgba(0,0,0,0.5)',
            border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10,
            padding: isMobile ? '4px 10px' : '6px 14px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4,
            color: '#fff', fontSize: isMobile ? 12 : 15, fontWeight: 600,
            backdropFilter: 'blur(4px)'
          }}
        >
          <span style={{ fontSize: isMobile ? 14 : 18 }}>{showWordBank ? '✕' : '\ud83d\udccb'}</span>
          <span>{foundWords.length}/{(currentLevel?.word_hints || []).length}</span>
        </button>

        {/* Almost There nudge */}
        {foundWords.length > 0 && foundWords.length === (currentLevel?.word_hints || []).length - 1 && (
          <div style={{
            position: 'absolute', top: isMobile ? 48 : 52, left: isMobile ? 8 : 12,
            background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)',
            color: '#fbbf24', padding: '4px 12px', borderRadius: 8,
            fontSize: 11, fontWeight: 600, animation: 'pulse 2s ease infinite',
            backdropFilter: 'blur(4px)', zIndex: 10
          }}>
            Almost there! 1 word left!
          </div>
        )}

        {/* Word Basket moved to right side TikTok column */}

        {/* Dim overlay — closes Word Bank on tap */}
        {showWordBank && (
          <div
            onClick={() => setShowWordBank(false)}
            style={{
              position: 'absolute', inset: 0, zIndex: 10
            }}
          />
        )}

        {/* Word Bank — slide-out panel from left */}
        <div style={{
          position: 'absolute', left: 0, top: 0, zIndex: 11,
          width: isMobile ? 170 : 260,
          maxHeight: '85%',
          transform: showWordBank ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.25s ease',
          background: 'rgba(10,10,30,0.85)',
          backdropFilter: 'blur(10px)',
          borderRight: '1px solid rgba(255,255,255,0.12)',
          borderTopRightRadius: 12, borderBottomRightRadius: 12,
          paddingTop: 36,
          overflowY: 'auto'
        }}>
          <WordBank
            wordHints={currentLevel?.word_hints || []}
            foundWords={foundWords}
            gridLayout={currentLevel?.grid_layout || []}
            compact={isMobile}
            showReveal={currentLevel?.is_training || currentLevel?.free_hints || (currentLevel?.reveal_word_count > 0)}
            revealCount={currentLevel?.reveal_word_count || 0}
          />
        </div>

        {/* Grid — full space, perfectly centered */}
        <CrosswordGrid
          gridLayout={currentLevel?.grid_layout || []}
          foundWords={foundWords}
          revealedCells={revealedCells}
          maxWidth={Math.max(120, gridMaxW)}
          maxHeight={Math.max(100, gridAreaH)}
          gridCellMax={effectiveGridCellMax}
          gridOpacity={gridOpacity}
          gridColor={gridColor}
        />
      </div>

      {/* ── BOTTOM: Icons row + Letter area ── */}
      <div style={{
        flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
        paddingBottom: 'max(12px, env(safe-area-inset-bottom, 8px))', position: 'relative', zIndex: 1
      }}>
        {/* Honeycomb with border */}
        <div style={{
          animation: shakeCircle ? 'shake 0.4s ease' : 'none',
          border: '1.5px solid rgba(255,255,255,0.15)',
          borderRadius: 16, padding: isMobile ? 8 : 10,
          background: 'rgba(0,0,0,0.15)'
        }}>
          <LetterCircle
            letters={letters}
            onWordComplete={checkWord}
            disabled={showComplete}
            radius={circleRadius}
            onSelectionChange={setHasSelection}
            hexSize={effectiveHexSize}
            hexGap={effectiveHexGap}
            hexStyle={activeHexStyle}
          />
        </div>

        {/* Icons row — below alphabet */}
        <div style={{
          display: 'flex', gap: isMobile ? 12 : 20, alignItems: 'center', justifyContent: 'center',
          marginTop: isMobile ? 8 : 14
        }}>
          <WordBasket count={basketCount} total={basketSize} justFilled={basketJustFilled} btnSize={iconBtnSize} bonusWords={bonusFound} showParticle={showBonusParticle} showConfetti={showConfetti} />
          <button onClick={() => useHint('letter')} style={{
            background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(245,158,11,0.4)',
            borderRadius: '50%', width: iconBtnSize, height: iconBtnSize,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: '#fbbf24', lineHeight: 1
          }}>
            <span style={{ fontSize: isMobile ? 15 : 20 }}>💡</span>
            <span style={{ fontSize: isMobile ? 7 : 9, fontWeight: 700 }}>60</span>
          </button>
          <button onClick={() => useHint('word')} style={{
            background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(59,130,246,0.4)',
            borderRadius: '50%', width: iconBtnSize, height: iconBtnSize,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: '#60a5fa', lineHeight: 1
          }}>
            <span style={{ fontSize: isMobile ? 15 : 20 }}>⚡</span>
            <span style={{ fontSize: isMobile ? 7 : 9, fontWeight: 700 }}>120</span>
          </button>
          <button onClick={hasSelection ? undefined : shuffleLetters} disabled={hasSelection} style={{
            background: hasSelection ? 'rgba(100,100,100,0.2)' : 'rgba(0,0,0,0.45)',
            border: hasSelection ? '1px solid rgba(100,100,100,0.3)' : '1px solid rgba(16,185,129,0.4)',
            color: hasSelection ? '#666' : '#34d399', borderRadius: '50%', width: iconBtnSize, height: iconBtnSize,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: hasSelection ? 'not-allowed' : 'pointer', fontSize: isMobile ? 16 : 22,
            opacity: hasSelection ? 0.5 : 1
          }}>
            🔀
          </button>
        </div>
      </div>
    </div>
  );
};

export default WordPuzzleGame;
